import { useBackgroundQuery, useReadQuery } from '@apollo/client/react'
import { sharedMessages, useIntl } from '@faceup/localization'
import { useUser } from '@faceup/member'
import { twMerge } from '@faceup/ui'
import { Button, Flex, Spin } from '@faceup/ui-base'
import {
  type CaseRating,
  CaseStatusGroup,
  ChannelType,
  getTranslateReportLanguage,
  type Language,
  NUMBER_FOR_NO_LIMIT,
  Plan,
  removeTranslateReportLanguage,
  saveTranslateReportLanguage,
} from '@faceup/utils'
import type { ResultOf } from '@graphql-typed-document-node/core'
import {
  type ComponentProps,
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react'
import { graphql } from '../../__generated__'
import {
  getElementTopFromScrollBoxTop,
  getFirstVisibleItem,
  getFirstVisibleItemAfterElement,
  useFollowNewestOnUpdate,
  useInitialScrollToBottom,
  useLingerIntersection,
  useScrollAnchorLock,
} from '../../hooks'

import { NotRedactableWrapper } from '../Redaction'
import { TranslateMenu } from '../TranslateMenu'
import { Chat } from './Chat'
import { ChatForm, ChatFormPlaceholder } from './ChatForm'
import { PopupsProvider } from './PopupsProvider'
import { RelativeMessagesProvider } from './RelativeMessagesContext'
import { ReportRating } from './ReportRating'
import { ReportStatusAlert } from './ReportStatusAlert'

const ITEM_ATTRIBUTE = 'data-follow-up-message-id'
const FOLLOW_UP_MESSAGES_PAGE_SIZE = 10
const FOLLOW_UP_MESSAGES_EXPORT_PAGE_SIZE = NUMBER_FOR_NO_LIMIT

export const query = {
  CompanyReportFollowUpChatQuery: graphql(`
    query CompanyReportFollowUpChatQuery(
      $caseId: UUID!
      $sourceLanguage: Language
      $targetLanguage: Language
    ) {
      case(caseId: $caseId) {
        id
        channel {
          id
          sourceType
        }
        ... on CompanyReport {
          hasMemberFollowUpComment
          translation(sourceLanguage: $sourceLanguage, targetLanguage: $targetLanguage) {
            id
            sourceLanguage
            targetLanguage
          }
          status {
            id
            groupName
            name
          }
        }
        ... on Submission {
          status {
            id
            groupName
            name
          }
        }
        isNameHidden

        victim {
          id
        }

        organizationalUnit {
          id
          isE2EE
          billing {
            id
            plan
          }

          workingHours {
            id
            outsideWorkingHoursMessage
            timezone
            enabled
            days {
              name
              from
              to
            }
          }
        }

        ...CompanyChatForm_case
        ...Chat_case
        ...ReportStatusAlert_case
        ...ReportRating_case
      }
    }
  `),
  CompanyReportFollowUpActivitiesQuery: graphql(`
    query CompanyReportFollowUpActivitiesQuery(
      $caseId: UUID!
      $sourceLanguage: Language
      $targetLanguage: Language
      $followUpActivitiesFirst: Int!
      $followUpActivitiesAfter: String
      $followUpActivitiesNewestFirst: Boolean
      $isVictim: Boolean!
    ) {
      case(caseId: $caseId) {
        id
        followUpActivities(
          first: $followUpActivitiesFirst
          after: $followUpActivitiesAfter
          newestFirst: $followUpActivitiesNewestFirst
        ) {
          nextCursor
          items {
            id
            author {
              ... on CompanyVictim {
                id
              }

              ... on Member {
                id
              }
            }
            ...FollowUpChatMessage_followUpActivityNode
          }
        }
      }
    }
  `),
}

// BAD PRACTICE: DO NOT INSPIRE FROM THIS, SHOULD BE REFACTORED
export type CompanyReportFollowUpChatQuery = ResultOf<typeof query.CompanyReportFollowUpChatQuery>

const subscriptions = {
  NewFollowUpReportActivitySubscription: graphql(`
    subscription NewFollowUpReportActivitySubscription(
      $caseId: UUID!
      $sourceLanguage: Language
      $targetLanguage: Language
      $isVictim: Boolean!
    ) {
      newCompanyReportFollowUpActivity(input: { caseId: $caseId }) {
        id
        author {
          ... on CompanyVictim {
            id
          }

          ... on Member {
            id
          }
        }
        ...FollowUpChatMessage_followUpActivityNode
      }
    }
  `),
}

type ChatWrapperProps = {
  isExportMode?: boolean
  caseId: string
  userId: string

  onMessageSend?: () => void
  onMessageSeen?: () => void
  fileDownloadHandler?: (blob: Blob, name: string) => Promise<void>
  onRatingChange?: (rating: CaseRating) => void

  relativeMessages: ComponentProps<typeof RelativeMessagesProvider>['relativeMessages']
  sourceLanguage?: Language | null
  targetLanguage?: Language | null
  input: ComponentProps<typeof ChatForm>['input']
  isLoading?: boolean
  firstUnreadMessageId?: string
  _wrapWithPermissionTooltip?: ComponentProps<typeof ChatForm>['_wrapWithPermissionTooltip']
}

export const ChatWrapper = ({
  isExportMode = false,
  caseId,
  userId,
  onMessageSend,
  onMessageSeen,
  onRatingChange,
  fileDownloadHandler,
  relativeMessages,
  sourceLanguage: defaultSourceLanguage,
  targetLanguage: defaultTargetLanguage,
  input,
  isLoading,
  firstUnreadMessageId,
  // This component is used in multiple places and in one of those places we need to wrap some content with a component
  //  this prop caters for that
  _wrapWithPermissionTooltip,
}: ChatWrapperProps) => {
  const { formatMessage } = useIntl()
  const chatRef = useRef<HTMLDivElement>(null)
  const chatContentRef = useRef<HTMLDivElement>(null)
  const showMoreButtonRef = useRef<HTMLButtonElement>(null)
  // Whether the user was at the bottom of the chat at the moment of the last
  // scroll event. Used to decide if a subscription-arrived message should
  // auto-scroll into view.
  const wasAtBottomRef = useRef(true)
  // Armed when the local user sends a message so that their own message — which
  // arrives back through the subscription — always scrolls into view, even if
  // they had scrolled up (e.g. reading history / after "Show more"). Consumed
  // by useFollowNewestOnUpdate on the next newest-message change.
  const forceFollowRef = useRef(false)
  // Stable wrappers for caller-provided callbacks. Without these, `memo()` on
  // FollowUpChatMessage is defeated by inline arrows from akutan/follow-up
  // (`onMessageSeen={() => track(...)}`) — new identity every render means
  // every message re-renders on every state change in this wrapper, which
  // makes "Show more" flicker as 30+ messages re-render 4-5x per fetch.
  const onMessageSeenRef = useRef(onMessageSeen)
  onMessageSeenRef.current = onMessageSeen
  const stableOnMessageSeen = useCallback(() => {
    onMessageSeenRef.current?.()
  }, [])
  const fileDownloadHandlerRef = useRef(fileDownloadHandler)
  fileDownloadHandlerRef.current = fileDownloadHandler
  // Gate the wrapper on a boolean rather than the handler identity so it stays
  // stable across renders even when callers pass an inline arrow. The
  // `undefined` branch is preserved because `downloadFileWithReportKey`
  // distinguishes the no-handler case (use default) from the handler-present
  // case.
  const hasFileDownloadHandler = Boolean(fileDownloadHandler)
  const stableFileDownloadHandler = useMemo(
    () =>
      hasFileDownloadHandler
        ? async (blob: Blob, name: string) => {
            await fileDownloadHandlerRef.current?.(blob, name)
          }
        : undefined,
    [hasFileDownloadHandler]
  )
  const [savedSourceLanguage, savedTargetLanguage] = getTranslateReportLanguage(caseId)
  const [internalSourceLanguage, setInternalSourceLanguage] = useState<Language | 'auto' | null>(
    () => {
      if (defaultSourceLanguage) {
        return defaultSourceLanguage
      }
      if (savedSourceLanguage && savedSourceLanguage !== 'auto') {
        return savedSourceLanguage
      }
      return null
    }
  )
  const [internalTargetLanguage, setInternalTargetLanguage] = useState<Language | null>(
    () => defaultTargetLanguage ?? savedTargetLanguage ?? null
  )

  // Derive the active language rather than syncing props into state with an
  // effect. The prop (akutan's report-level content-language selector) wins
  // whenever the caller drives translation; otherwise the internal state
  // (follow-up's TranslateMenu) is in control.
  //
  // `undefined` means "this caller doesn't drive language" (the follow-up app
  // passes no props) → fall back to internal state. `null` is an intentional
  // value (akutan's "show original" clears the saved language), so it must flow
  // through to clear the chat back to the original — hence `!== undefined`
  // rather than `??`. Deriving instead of syncing also avoids a wasted
  // translation fetch: a prop change lands in the same render rather than after
  // an effect re-render with stale languages.
  const sourceLanguage =
    defaultSourceLanguage !== undefined ? defaultSourceLanguage : internalSourceLanguage
  const targetLanguage =
    defaultTargetLanguage !== undefined ? defaultTargetLanguage : internalTargetLanguage
  const [isLoadingOlderMessages, startLoadOlderMessagesTransition] = useTransition()
  // Anchor captured for the in-flight "Show more" load. Kept so the lock can be
  // restarted once the paging transition settles (see the effect below).
  const pendingAnchorRef = useRef<{ anchorId: string; anchorTopFromScrollBoxTop: number } | null>(
    null
  )
  const [isInitialScrollDone, setIsInitialScrollDone] = useState(false)
  const followUpActivitiesPageSize = isExportMode
    ? FOLLOW_UP_MESSAGES_EXPORT_PAGE_SIZE
    : FOLLOW_UP_MESSAGES_PAGE_SIZE
  const followUpActivitiesNewestFirst = !isExportMode
  const sourceLanguageForQuery = sourceLanguage === 'auto' ? null : sourceLanguage
  const targetLanguageForQuery = targetLanguage
  // Derived from the app context rather than report data, so it's available
  // without waiting on `CompanyReportFollowUpChatQuery` (see the parallelism
  // note below) — mirrors the same app-based check in useMarkMessagesAsRead.
  const { application } = useUser()
  const isVictim = application === 'follow-up'

  // useBackgroundQuery (not useSuspenseQuery) so both requests start in parallel.
  // useSuspenseQuery suspends on the first call, unmounting before the second
  // can fire — a waterfall. useBackgroundQuery kicks off the request without
  // suspending; useReadQuery below suspends only when the data is read.
  const [chatQueryRef] = useBackgroundQuery(query.CompanyReportFollowUpChatQuery, {
    variables: {
      caseId,
      sourceLanguage: sourceLanguageForQuery,
      targetLanguage: targetLanguageForQuery,
    },
  })
  const [
    followUpActivitiesQueryRef,
    { subscribeToMore: subscribeToMoreFollowUpActivities, fetchMore: fetchMoreFollowUpActivities },
  ] = useBackgroundQuery(query.CompanyReportFollowUpActivitiesQuery, {
    variables: {
      caseId,
      sourceLanguage: sourceLanguageForQuery,
      targetLanguage: targetLanguageForQuery,
      followUpActivitiesFirst: followUpActivitiesPageSize,
      followUpActivitiesAfter: null,
      followUpActivitiesNewestFirst,
      isVictim,
    },
  })

  const { data } = useReadQuery(chatQueryRef)
  const { data: followUpActivitiesData } = useReadQuery(followUpActivitiesQueryRef)

  const followUpActivitiesConnection = followUpActivitiesData?.case?.followUpActivities
  const followUpActivitiesItems = useMemo(
    () => followUpActivitiesConnection?.items ?? [],
    [followUpActivitiesConnection?.items]
  )

  const { start: startAnchorLock, stop: stopAnchorLock } = useScrollAnchorLock({
    scrollRef: chatRef,
    itemAttribute: ITEM_ATTRIBUTE,
  })

  const subscribeToNewMessages = useCallback(() => {
    const unsubscribe = subscribeToMoreFollowUpActivities({
      document: subscriptions.NewFollowUpReportActivitySubscription,
      variables: {
        caseId,
        sourceLanguage: sourceLanguageForQuery,
        targetLanguage: targetLanguageForQuery,
        isVictim,
      },
      updateQuery: (_, { subscriptionData, complete, previousData }) => {
        if (!complete || !subscriptionData?.data || !previousData.case) {
          return
        }

        const incomingActivities = subscriptionData.data.newCompanyReportFollowUpActivity

        const existingIds = new Set(
          previousData.case.followUpActivities?.items?.map(item => item?.id)
        )
        const newActivities = (incomingActivities ?? []).filter(
          (activity: { id: string }) => !existingIds.has(activity.id)
        )

        if (!newActivities.length) {
          return
        }

        return {
          ...previousData,
          case: {
            ...previousData.case,
            followUpActivities: {
              ...previousData.case.followUpActivities,
              items: followUpActivitiesNewestFirst
                ? [...newActivities, ...(previousData.case.followUpActivities?.items ?? [])]
                : [...(previousData.case.followUpActivities?.items ?? []), ...newActivities],
            },
          },
        }
      },
    })

    return unsubscribe
  }, [
    caseId,
    followUpActivitiesNewestFirst,
    isVictim,
    sourceLanguageForQuery,
    subscribeToMoreFollowUpActivities,
    targetLanguageForQuery,
  ])

  const [isFirstMessage, setIsFirstMessage] = useState(false)

  const setMessage = () => {
    onMessageSend?.()
    // Always follow the user's own outgoing message to the bottom, regardless
    // of current scroll position.
    forceFollowRef.current = true
    if (!isFirstMessage) {
      setIsFirstMessage(true)
    }
  }

  // Reset paging on chat query change; also stops any running anchor lock on
  // re-run and on unmount. Resetting isInitialScrollDone disarms the auto-load
  // observer during the refetch window so it can't fire while data is in flux.
  // biome-ignore lint/correctness/useExhaustiveDependencies: setters from useState are stable
  useEffect(() => {
    setIsInitialScrollDone(false)
    return stopAnchorLock
  }, [caseId, sourceLanguage, targetLanguage, stopAnchorLock])

  useLayoutEffect(() => subscribeToNewMessages?.() ?? undefined, [subscribeToNewMessages])

  const followUpActivitiesNextCursor = followUpActivitiesConnection?.nextCursor ?? null
  const hasOlderMessages = !isExportMode && followUpActivitiesNextCursor !== null

  const handleInitialScrollDone = useCallback(() => setIsInitialScrollDone(true), [])
  useInitialScrollToBottom({
    scrollRef: chatRef,
    ready: Boolean(followUpActivitiesConnection),
    done: isInitialScrollDone,
    onDone: handleInitialScrollDone,
  })

  useFollowNewestOnUpdate({
    scrollRef: chatRef,
    newestId: followUpActivitiesItems[0]?.id,
    enabled: isInitialScrollDone,
    isAtBottomRef: wasAtBottomRef,
    forceFollowRef,
  })

  const loadOlderMessages = useCallback(() => {
    const chatElement = chatRef.current
    if (
      !chatElement ||
      !hasOlderMessages ||
      isLoadingOlderMessages ||
      !followUpActivitiesNextCursor
    ) {
      return
    }

    const anchorElement =
      getFirstVisibleItemAfterElement(chatElement, ITEM_ATTRIBUTE, showMoreButtonRef.current) ??
      getFirstVisibleItem(chatElement, ITEM_ATTRIBUTE)
    if (!anchorElement) {
      return
    }
    const anchorId = anchorElement.dataset['followUpMessageId']
    if (!anchorId) {
      return
    }
    const anchorTopFromScrollBoxTop = getElementTopFromScrollBoxTop(chatElement, anchorElement)

    const anchorParams = { anchorId, anchorTopFromScrollBoxTop }
    pendingAnchorRef.current = anchorParams
    // Start lock BEFORE fetch so it catches all DOM changes as new messages mount
    // and asynchronously fill in their decrypted content.
    startAnchorLock(anchorParams)

    // Drive the loading state off the transition rather than a manual flag.
    // Wrapping the suspense-query fetchMore in a transition keeps the existing
    // messages on screen (no Suspense fallback) and exposes progress through
    // `isLoadingOlderMessages` (the transition's isPending). The lock is
    // restarted once the transition settles (see the effect below), not here —
    // the spinner toggles off only when isPending flips back, after this async
    // function returns.
    startLoadOlderMessagesTransition(async () => {
      try {
        const result = await fetchMoreFollowUpActivities({
          variables: {
            caseId,
            sourceLanguage: sourceLanguageForQuery,
            targetLanguage: targetLanguageForQuery,
            followUpActivitiesFirst: followUpActivitiesPageSize,
            followUpActivitiesAfter: followUpActivitiesNextCursor,
            followUpActivitiesNewestFirst,
            isVictim,
          },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!prev.case || !fetchMoreResult?.case) {
              return prev
            }

            const existingActivities = prev.case.followUpActivities?.items ?? []
            const existingIds = new Set(
              existingActivities.map((activity: { id: string }) => activity.id)
            )
            const olderActivities = (fetchMoreResult.case.followUpActivities?.items ?? []).filter(
              (activity: { id: string }) => !existingIds.has(activity.id)
            )

            // Cast: see the subscription updateQuery above — the items from
            // fetchMoreResult are typed under a different fragment-mask use
            // site than `prev.case.followUpActivities.items`, even though they
            // resolve to the same shape at runtime.
            return {
              ...prev,
              case: {
                ...prev.case,
                followUpActivities: {
                  ...prev.case.followUpActivities,
                  nextCursor: fetchMoreResult.case.followUpActivities?.nextCursor ?? null,
                  items: followUpActivitiesNewestFirst
                    ? [...existingActivities, ...olderActivities]
                    : [...olderActivities, ...existingActivities],
                },
              },
            } as typeof prev
          },
        })

        const loadedItemsCount = result.data?.case?.followUpActivities?.items?.length ?? 0
        if (loadedItemsCount <= 0) {
          stopAnchorLock()
          pendingAnchorRef.current = null
        }
      } catch {
        // Callers fire-and-forget this loader; rethrowing would surface as an
        // unhandled rejection. Surface failures via Apollo's own error state.
        stopAnchorLock()
        pendingAnchorRef.current = null
      }
    })
  }, [
    caseId,
    fetchMoreFollowUpActivities,
    followUpActivitiesNewestFirst,
    followUpActivitiesNextCursor,
    followUpActivitiesPageSize,
    hasOlderMessages,
    isLoadingOlderMessages,
    isVictim,
    sourceLanguageForQuery,
    startAnchorLock,
    stopAnchorLock,
    targetLanguageForQuery,
  ])

  // Restart the lock for one more stable burst once the paging transition has
  // settled. The "Show more" button toggling its loading state off (spinner in
  // / spinner out) shifts top-of-content by a few px, and that lands only when
  // isPending flips back to false — after the load's async function (and the
  // first lock, started before the fetch) has already finished. Reacting to
  // isLoadingOlderMessages going false fires this exactly when the spinner is
  // removed, matching the pre-transition timing.
  const wasLoadingOlderMessagesRef = useRef(false)
  useEffect(() => {
    const justSettled = wasLoadingOlderMessagesRef.current && !isLoadingOlderMessages
    wasLoadingOlderMessagesRef.current = isLoadingOlderMessages
    if (!justSettled) {
      return
    }
    const anchorParams = pendingAnchorRef.current
    pendingAnchorRef.current = null
    if (!anchorParams) {
      return
    }
    void waitForRenderedFrame().then(() => startAnchorLock(anchorParams))
  }, [isLoadingOlderMessages, startAnchorLock])

  // Auto-load older messages when the "Show more" button has been visible for
  // 200ms. A brief flyby (e.g. macOS momentum that exposes the button before
  // settling deeper) doesn't trigger a fetch.
  useLingerIntersection({
    targetRef: showMoreButtonRef,
    rootRef: chatRef,
    delayMs: 200,
    enabled: hasOlderMessages && isInitialScrollDone,
    onLinger: () => void loadOlderMessages(),
  })

  const aCase = data?.case ?? null
  if (!aCase || !followUpActivitiesConnection) {
    if (isExportMode) {
      return null
    }
    return (
      <>
        <div
          className={twMerge(
            'flex min-h-[150px] overflow-auto [overflow-anchor:auto]',
            !isExportMode && 'max-h-[365px]',
            'items-center justify-center'
          )}
        >
          <Spin />
        </div>
        <NotRedactableWrapper>
          <div
            style={{
              display: 'flex',
              flexFlow: 'column nowrap',
              alignItems: 'center',
            }}
          >
            <ChatFormPlaceholder input={input} />
          </div>
        </NotRedactableWrapper>
      </>
    )
  }

  // Victims only need the translate menu once a handler has written to them.
  // The chat may already contain system or victim-authored activities the
  // moment the case is created — those should not surface translation UI.
  // Read from a case-level field (not the loaded page): with pagination the
  // member's message can sit outside the currently loaded window, so scanning
  // `followUpActivitiesItems` would hide the menu on longer threads.
  const hasMemberMessage = aCase.__typename === 'CompanyReport' && aCase.hasMemberFollowUpComment

  const showTranslateMenu =
    userId === aCase.victim?.id &&
    !aCase?.organizationalUnit?.isE2EE &&
    Boolean(hasMemberMessage) &&
    [Plan.Basic, Plan.Standard, Plan.Premium].includes(
      aCase?.organizationalUnit?.billing?.plan ?? Plan.Unregistered
    )

  return (
    <PopupsProvider report={aCase}>
      <RelativeMessagesProvider relativeMessages={relativeMessages}>
        {/* overflow-anchor:auto lets the browser keep the visible content anchored
            when items are prepended. When supported, the RAF-based anchor lock in
            ChatWrapper short-circuits and does not run, so the two strategies don't
            fight (e.g. on macOS where the RAF writes would interrupt momentum
            scrolling). The RAF lock is the fallback path for browsers that don't
            support overflow-anchor.

            max-h-[365px] (skipped in export mode where the whole transcript,
            potentially thousands of messages, is mounted at once) caps the scroll
            container height. */}
        <div
          ref={chatRef}
          className={twMerge(
            'flex min-h-[150px] overflow-auto [overflow-anchor:auto]',
            !isExportMode && 'max-h-[365px]',
            followUpActivitiesItems.length === 0 && 'items-center justify-center'
          )}
          onScroll={event => {
            const el = event.currentTarget
            wasAtBottomRef.current = el.scrollTop + el.clientHeight >= el.scrollHeight - 20
          }}
        >
          <div
            data-test='follow-up-chat'
            ref={chatContentRef}
            className={twMerge('flex min-w-0 flex-1 flex-col', !hasOlderMessages && 'pt-4')}
          >
            {hasOlderMessages && (
              <div className='flex justify-center pt-2 pb-3'>
                <Button
                  ref={showMoreButtonRef}
                  type='link'
                  loading={isLoadingOlderMessages}
                  disabled={isLoadingOlderMessages}
                  onClick={() => void loadOlderMessages()}
                >
                  {formatMessage(sharedMessages.showMore)}
                </Button>
              </div>
            )}
            <Chat
              firstUnreadMessageId={firstUnreadMessageId}
              report={aCase ?? null}
              followUpActivities={followUpActivitiesItems}
              subscribeToNewMessages={subscribeToNewMessages}
              userId={userId}
              fileDownloadHandler={stableFileDownloadHandler}
              onMessageSeen={stableOnMessageSeen}
              newestMessagesLoadedFirst={followUpActivitiesNewestFirst}
            />
          </div>
        </div>

        <Flex gap='24px' vertical>
          {!isExportMode && (
            <ReportStatusAlert userId={userId} case={aCase} isFirstMessage={isFirstMessage} />
          )}
          {aCase.channel.sourceType !== ChannelType.Survey && (
            <ReportRating userId={userId} report={aCase} onRatingChange={onRatingChange} />
          )}
        </Flex>

        {!isExportMode &&
          aCase?.status?.groupName &&
          aCase?.status?.groupName !== CaseStatusGroup.Closed && (
            <NotRedactableWrapper>
              <div
                style={{
                  display: 'flex',
                  flexFlow: 'column nowrap',
                  alignItems: 'center',
                }}
              >
                {showTranslateMenu && (
                  <div style={{ marginBottom: '1rem' }}>
                    <TranslateMenu
                      defaultLanguages={[sourceLanguage ?? 'auto', targetLanguage]}
                      onTranslate={(source, target) => {
                        // startTransition keeps the existing chat content
                        // visible while useSuspenseQuery refetches with the
                        // new languages, instead of unmounting to the parent
                        // Suspense fallback.
                        startTransition(() => {
                          setInternalSourceLanguage(source)
                          setInternalTargetLanguage(target)
                        })
                        saveTranslateReportLanguage(caseId, [source, target])
                      }}
                      onShowOriginal={() => {
                        startTransition(() => {
                          setInternalSourceLanguage(null)
                          setInternalTargetLanguage(null)
                        })
                        removeTranslateReportLanguage(caseId)
                      }}
                      translations={{
                        detectLanguage: formatMessage(relativeMessages.detectLanguage),
                        showOriginal: formatMessage(relativeMessages.showOriginal),
                        translate: formatMessage(relativeMessages.translate),
                        to: formatMessage(relativeMessages.to),
                        sameLanguageError: formatMessage(relativeMessages.sameLanguageError),
                        disabledReasonE2EE: formatMessage(relativeMessages.disabledReasonE2EE),
                        onUseWarningTitle: formatMessage(relativeMessages.onUseWarningTitle),
                        onUseWarningContent: formatMessage(relativeMessages.onUseWarningContent),
                        onUseWarningDoNotShowAgain: formatMessage(
                          relativeMessages.onUseWarningDoNotShowAgain
                        ),
                      }}
                      {...(aCase.organizationalUnit?.isE2EE ? { disabledReason: 'e2ee' } : {})}
                    />
                  </div>
                )}
                <ChatForm
                  report={aCase ?? null}
                  sendClicked={setMessage}
                  userId={userId}
                  input={input}
                  _wrapWithPermissionTooltip={_wrapWithPermissionTooltip}
                  isLoading={isLoading}
                />
              </div>
            </NotRedactableWrapper>
          )}
      </RelativeMessagesProvider>
    </PopupsProvider>
  )
}

const waitForAnimationFrame = () => {
  return new Promise<void>(resolve => requestAnimationFrame(() => resolve()))
}

// Wait for a fully painted frame, not just the next commit. The first rAF
// fires after React commits; the second fires once the browser has actually
// laid out and painted the resulting DOM. Without the second rAF, sizes read
// here can still be from the previous layout pass. Do NOT simplify to a single
// rAF — that silently reintroduces the post-paint scroll jump that motivated
// this helper.
const waitForRenderedFrame = async () => {
  await waitForAnimationFrame()
  await waitForAnimationFrame()
}
